import { useState, useMemo } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import {
  Home,
  Mail,
  Users,
  MapPin,
  FileText,
  LogOut,
  Menu,
  X,
  Building2,
  Sparkles,
  Globe,
  PoundSterling,
  TrendingUp,
  BookOpen,
  Snowflake,
  Briefcase,
  ClipboardList,
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { clsx } from 'clsx'
import type { UserRole } from '@/types'
import type { LucideIcon } from 'lucide-react'

interface NavItem {
  name: string
  href: string
  icon: LucideIcon
}

/**
 * Get navigation items based on user role
 */
function getNavigationForRole(role?: UserRole): NavItem[] {
  const baseNav: NavItem[] = [
    { name: 'Dashboard', href: '/', icon: Home },
  ]

  if (role === 'CLIENT') {
    return [
      { name: 'My Letters', href: '/', icon: Mail },
    ]
  }

  if (role === 'SALES') {
    // Sales role: Planning Sales, Website Sales, My Commission, Sales Guides
    return [
      ...baseNav,
      { name: 'Planning Sales', href: '/sales/planning', icon: MapPin },
      { name: 'Website Sales', href: '/sales/website', icon: Globe },
      { name: 'My Commission', href: '/sales/commission', icon: PoundSterling },
      { name: 'Sales Guides', href: '/sales/guides', icon: BookOpen },
    ]
  }

  if (role === 'ADMIN') {
    // Admin role: Full access including sales overview and payroll
    return [
      ...baseNav,
      { name: 'Letters', href: '/letters', icon: Mail },
      { name: 'Cold Letters', href: '/cold-letters', icon: Snowflake },
      { name: 'Cold Business', href: '/cold-business', icon: Briefcase },
      { name: 'Clients', href: '/clients', icon: Building2 },
      { name: 'Territories', href: '/territories', icon: MapPin },
      { name: 'Applications', href: '/applications', icon: FileText },
      { name: 'Audit', href: '/audit', icon: ClipboardList },
      { name: 'Users', href: '/users', icon: Users },
      { name: 'Sales Overview', href: '/admin/sales', icon: TrendingUp },
      { name: 'Planning Prospects', href: '/sales/planning', icon: Sparkles },
      { name: 'Website Prospects', href: '/sales/website', icon: Globe },
      { name: 'Payroll', href: '/admin/payroll', icon: PoundSterling },
    ]
  }

  // Marketing role: Standard letter workflow
  return [
    ...baseNav,
    { name: 'Letters', href: '/letters', icon: Mail },
    { name: 'Cold Letters', href: '/cold-letters', icon: Snowflake },
    { name: 'Cold Business', href: '/cold-business', icon: Briefcase },
    { name: 'Clients', href: '/clients', icon: Building2 },
    { name: 'Territories', href: '/territories', icon: MapPin },
    { name: 'Applications', href: '/applications', icon: FileText },
  ]
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()

  // Get navigation items based on user role
  const navigation = useMemo(() => getNavigationForRole(user?.role), [user?.role])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-y-0 left-0 z-50 w-72 sidebar transform transition-transform duration-300 ease-in-out lg:hidden',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex items-center justify-between h-24 px-4 border-b border-slate-800/50">
          <img src="/logo.png" alt="Trade Prospector" className="h-20" />
          <button onClick={() => setSidebarOpen(false)} className="p-1 rounded-lg hover:bg-slate-800/50">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <nav className="mt-4 px-3 space-y-1">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => clsx(
                isActive ? 'sidebar-item-active' : 'sidebar-item'
              )}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-grow sidebar">
          {/* Logo */}
          <div className="flex items-center h-24 px-6 border-b border-slate-800/50">
            <img src="/logo.png" alt="Trade Prospector" className="h-20" />
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 mt-6 px-4 space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) => clsx(
                  isActive ? 'sidebar-item-active' : 'sidebar-item'
                )}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>
          
          {/* User section */}
          <div className="p-4 border-t border-slate-800/50">
            <div className="flex items-center p-3 rounded-xl bg-slate-800/30">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {user?.fullName?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">{user?.fullName}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
              <button 
                onClick={handleLogout}
                className="p-2 text-slate-500 hover:text-slate-300 hover:bg-slate-700/50 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Mobile header */}
        <div className="sticky top-0 z-30 flex items-center h-14 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800/50 lg:hidden px-4">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-800/50"
          >
            <Menu className="w-5 h-5 text-slate-400" />
          </button>
          <div className="ml-3 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#f39c13] to-[#d68910] flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-sm font-semibold"><span className="text-white">Trade</span> <span className="text-[#f39c13]">Prospector</span></span>
          </div>
        </div>

        {/* Page content */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
